import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Plug, CheckCircle2, AlertTriangle, Loader2, ExternalLink,
  RefreshCw, LogOut, ShieldCheck, FileText, Zap, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin → Integrations
 *
 * Third-party integration management surface. Phase 1 ships:
 *   • Xero (accounting) — OAuth connect / refresh / disconnect / live status
 *   • Yardstik (background checks) — service status
 *   • Enterprise API — placeholder (Phase 2)
 *
 * The Xero block is the reason this page exists right now: admins were
 * seeing a ghost "connected=false, needs_reauth=false" state and couldn't
 * tell whether the refresh was pending or dead. This UI reads the truthful
 * status endpoint and routes the re-auth flow through a new tab so the
 * settings screen retains its scroll/state while Xero's consent page loads.
 */
const AdminIntegrations = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Xero ────────────────────────────────────────────────
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(true);
  const [xeroBusy, setXeroBusy] = useState(false);
  const [xeroTestResult, setXeroTestResult] = useState(null);

  // ── Yardstik ────────────────────────────────────────────
  const [yardstikStatus, setYardstikStatus] = useState(null);
  const [yardstikLoading, setYardstikLoading] = useState(true);

  // Iter-220i+ — Mobile-friendly diagnostic panel. Admins on mobile
  // (no DevTools) can read the full /diag JSON, copy it, and email/
  // paste it for support without needing browser console access.
  const [xeroDiag, setXeroDiag] = useState(null);
  const [xeroDiagLoading, setXeroDiagLoading] = useState(false);
  const [xeroDiagOpen, setXeroDiagOpen] = useState(false);
  const [xeroAuthUrl, setXeroAuthUrl] = useState(null);

  const loadXeroStatus = useCallback(async () => {
    setXeroLoading(true);
    try {
      const res = await api.get('/integrations/xero/status');
      setXeroStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Xero status');
    } finally {
      setXeroLoading(false);
    }
  }, []);

  const loadXeroDiag = useCallback(async () => {
    setXeroDiagLoading(true);
    try {
      // Fetch the diag JSON and the would-be auth_url in parallel so
      // the admin can paste both with one tap.
      const [diagRes, authRes] = await Promise.all([
        api.get('/integrations/xero/diag'),
        api.get('/integrations/xero/auth').catch(() => ({ data: { auth_url: '(auth endpoint failed — see diag)' } })),
      ]);
      setXeroDiag(diagRes.data);
      setXeroAuthUrl(authRes.data?.auth_url || null);
      setXeroDiagOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load diagnostic');
    } finally {
      setXeroDiagLoading(false);
    }
  }, []);

  const copyDiagBundle = () => {
    const bundle = {
      environment: window.location.host,
      fetched_at: new Date().toISOString(),
      xero_status: xeroStatus,
      xero_diag: xeroDiag,
      xero_auth_url: xeroAuthUrl,
    };
    const text = JSON.stringify(bundle, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      toast.success('Diagnostic bundle copied — paste it into support');
    } else {
      // Fallback for older mobile browsers — select the textarea
      const ta = document.getElementById('xero-diag-bundle-textarea');
      if (ta) {
        ta.select();
        try { document.execCommand('copy'); toast.success('Copied'); }
        catch { toast.error('Long-press the box below to copy manually'); }
      }
    }
  };

  const loadYardstikStatus = useCallback(async () => {
    setYardstikLoading(true);
    try {
      const res = await api.get('/background-checks/status');
      setYardstikStatus(res.data);
    } catch (e) {
      // Non-critical — background check admin is itself guarded
      setYardstikStatus({ enabled: false, provider: 'yardstik' });
    } finally {
      setYardstikLoading(false);
    }
  }, []);

  useEffect(() => {
    loadXeroStatus();
    loadYardstikStatus();
  }, [loadXeroStatus, loadYardstikStatus]);

  // Surface the callback banner after the redirect-back from Xero.
  useEffect(() => {
    const xeroQ = searchParams.get('xero');
    if (xeroQ === 'connected') toast.success('Xero connected');
    if (xeroQ === 'error') toast.error(`Xero authorization failed: ${searchParams.get('reason') || 'unknown'}`);
  }, [searchParams]);

  const handleConnectXero = async () => {
    setXeroBusy(true);
    try {
      const res = await api.get('/integrations/xero/auth');
      if (res.data?.auth_url) {
        // Open the consent page in a new tab so the admin never loses
        // their place in settings. On return, /callback redirects back
        // to this page with ?xero=connected.
        window.open(res.data.auth_url, '_blank', 'noopener,noreferrer');
        toast.info('Xero authorization opened in a new tab');
      } else {
        toast.error('Failed to start Xero authorization');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate Xero OAuth');
    } finally {
      setXeroBusy(false);
    }
  };

  const handleRefreshXero = async () => {
    setXeroBusy(true);
    try {
      await api.post('/integrations/xero/refresh-token');
      toast.success('Xero token refreshed');
      await loadXeroStatus();
    } catch (e) {
      const reason = e.response?.data?.detail || 'Refresh failed';
      toast.error(`Xero refresh failed: ${reason}`);
      await loadXeroStatus();
    } finally {
      setXeroBusy(false);
    }
  };

  const handleTestXero = async () => {
    setXeroBusy(true);
    setXeroTestResult(null);
    try {
      const res = await api.get('/integrations/xero/test-connection');
      setXeroTestResult(res.data);
      if (res.data?.success) {
        toast.success(`Connected to ${res.data.organization}`);
      } else {
        toast.error(res.data?.error || 'Xero connection test failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Xero test failed');
    } finally {
      setXeroBusy(false);
    }
  };

  const handleDisconnectXero = async () => {
    if (!window.confirm('Disconnect Xero? You will need to re-authorize to resume syncing.')) return;
    setXeroBusy(true);
    try {
      await api.post('/integrations/xero/disconnect');
      toast.success('Xero disconnected');
      setXeroTestResult(null);
      await loadXeroStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setXeroBusy(false);
    }
  };

  // ── Status-chip helper ──────────────────────────────────
  const xeroChip = () => {
    if (!xeroStatus) return { label: 'Loading…', className: 'bg-gray-100 text-gray-500' };
    if (!xeroStatus.configured) return { label: 'Not configured', className: 'bg-red-100 text-red-700' };
    if (xeroStatus.needs_reauth) return { label: 'Re-authorize required', className: 'bg-amber-100 text-amber-700' };
    if (xeroStatus.connected && !xeroStatus.token_expired) return { label: 'Connected', className: 'bg-emerald-100 text-emerald-700' };
    if (xeroStatus.connected && xeroStatus.token_expired) return { label: 'Token expired — refresh', className: 'bg-amber-100 text-amber-700' };
    return { label: 'Not connected', className: 'bg-gray-100 text-gray-600' };
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-integrations">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-900">Integrations</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* ──────── Zapier & CRM (Super Admin) ──────── */}
        <Card data-testid="zapier-card">
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">Zapier &amp; CRM (Builder Prime, HubSpot, Salesforce…)</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Native lead sync + automated workflows. Manage API keys,
                  webhook subscriptions, delivery history and OAuth 2.0
                  authorizations. Financial actions are blocked from Zapier
                  by design.
                </CardDescription>
              </div>
            </div>
            <a
              href="/admin/zapier"
              className="text-sm font-medium text-indigo-600 hover:underline whitespace-nowrap"
              data-testid="open-zapier-integrations-link"
            >
              Open →
            </a>
          </CardHeader>
        </Card>

        {/* ──────── Xero ──────── */}
        <Card data-testid="xero-card">
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <CardTitle className="text-base">Xero — Accounting & Invoicing</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Sync invoices, contacts, and payments with your Xero organisation.
                </CardDescription>
              </div>
            </div>
            <Badge className={xeroChip().className} data-testid="xero-status-chip">{xeroChip().label}</Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            {xeroLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading Xero status…
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Row label="Organisation" value={xeroStatus?.tenant_name || '—'} />
                <Row label="Tenant ID" value={xeroStatus?.tenant_id ? xeroStatus.tenant_id.slice(0, 13) + '…' : '—'} mono />
                <Row label="Configured" value={xeroStatus?.configured ? 'Yes' : 'No'} />
                <Row label="Token expired" value={xeroStatus?.token_expired ? 'Yes' : 'No'} />
                <Row label="Last refresh attempt" value={xeroStatus?.last_refresh_attempt_at ? new Date(xeroStatus.last_refresh_attempt_at).toLocaleString() : '—'} />
                <Row label="Last refresh error" value={xeroStatus?.last_refresh_error || '—'} mono />
              </div>
            )}

            {xeroStatus?.needs_reauth && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800" data-testid="xero-reauth-banner">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Re-authorization required</p>
                  <p className="mt-0.5">
                    {xeroStatus.last_refresh_error
                      ? `Refresh failed: ${xeroStatus.last_refresh_error.split(':')[0]}. Click Connect to Xero to restore sync.`
                      : 'The current Xero token cannot be refreshed automatically. Click Connect to Xero to restore sync.'}
                  </p>
                </div>
              </div>
            )}

            {/* Iter-220h — Surface the exact redirect_uri the backend would
                send to Xero so admins can verify it matches the value
                registered in the Xero Developer Portal. The #1 cause of
                "invalid_redirect_uri" errors is a one-character mismatch
                (trailing slash, http vs https, missing /api/) between
                env and Xero portal. */}
            {xeroStatus?.redirect_uri && (
              <div
                className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs"
                data-testid="xero-redirect-uri-diag"
              >
                <div className="font-medium text-gray-700 mb-1 flex items-center gap-2">
                  Redirect URI sent to Xero
                  <button
                    type="button"
                    className="text-cyan-700 hover:underline text-[11px]"
                    onClick={() => {
                      navigator.clipboard?.writeText(xeroStatus.redirect_uri);
                    }}
                    data-testid="xero-redirect-uri-copy-btn"
                  >
                    Copy
                  </button>
                </div>
                <code
                  className="font-mono text-[11px] break-all text-gray-900 block"
                  data-testid="xero-redirect-uri-value"
                >
                  {xeroStatus.redirect_uri}
                </code>
                <p className="mt-2 text-[11px] text-gray-500 leading-snug">
                  This must <strong>exactly</strong> match the value registered at{' '}
                  <a
                    href="https://developer.xero.com/app/manage"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-700 underline"
                  >
                    developer.xero.com/app/manage
                  </a>
                  . No trailing slash. HTTPS. Domain must be{' '}
                  <code className="font-mono">app.ievntur.com</code> in production.
                </p>
              </div>
            )}

            {/* Iter-220i+ — Mobile-friendly diagnostic panel. Tap
                "Run diagnostic" → page calls /diag + /auth in parallel
                and displays everything. The "Copy diag bundle" button
                bundles status + diag + auth_url into a single JSON
                blob the admin can paste into support without DevTools. */}
            <div className="rounded-md border border-violet-200 bg-violet-50 p-3 text-xs">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-medium text-violet-900">
                  OAuth diagnostic
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-violet-300 text-violet-900 hover:bg-violet-100"
                  onClick={loadXeroDiag}
                  disabled={xeroDiagLoading}
                  data-testid="xero-diag-run-btn"
                >
                  {xeroDiagLoading ? 'Running…' : (xeroDiag ? 'Refresh' : 'Run diagnostic')}
                </Button>
              </div>
              <p className="text-[11px] text-violet-800 leading-snug">
                If Xero is returning "invalid_redirect_uri", tap above to
                show the env values the production backend is actually
                loading. Then tap "Copy diag bundle" and paste it into
                support.
              </p>

              {xeroDiagOpen && xeroDiag && (
                <div className="mt-3 space-y-2" data-testid="xero-diag-panel">
                  {/* Iter-220r — Auto-verdict headline. Tells the admin in
                      one line whether the config will work, and surfaces
                      the specific fix when it won't. Eliminates the need
                      to mentally diff schemes / hosts / whitespace. */}
                  <div
                    className={`rounded-lg p-3 border-2 ${
                      xeroDiag.verdict === 'pass'
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                        : 'bg-rose-50 border-rose-300 text-rose-900'
                    }`}
                    data-testid="xero-diag-verdict"
                  >
                    <div className="font-bold text-sm flex items-center gap-1.5">
                      {xeroDiag.verdict === 'pass' ? '✓ Configuration looks good' : `✗ ${xeroDiag.verdict_failures?.length || 1} problem(s) found`}
                    </div>
                    {xeroDiag.verdict !== 'pass' && Array.isArray(xeroDiag.verdict_failures) && (
                      <ul className="mt-2 space-y-1.5 text-[11px]">
                        {xeroDiag.verdict_failures.map((f, i) => (
                          <li key={i} className="bg-white/60 rounded p-2 border border-rose-200" data-testid={`xero-diag-failure-${f.code}`}>
                            <div className="font-semibold">{f.message}</div>
                            <div className="text-rose-700 mt-0.5">→ {f.fix}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Pass / fail summary row — at-a-glance for mobile. */}
                  <div className="grid grid-cols-1 gap-1.5">
                    <div
                      className={`rounded px-2 py-1.5 border text-[11px] flex items-start justify-between gap-2 ${
                        xeroDiag.matches_expected_production
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                          : 'bg-rose-50 border-rose-200 text-rose-900'
                      }`}
                      data-testid="xero-diag-matches-prod"
                    >
                      <span className="font-medium">Matches production?</span>
                      <span>{xeroDiag.matches_expected_production ? '✓ YES' : '✗ NO'}</span>
                    </div>

                    <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-redirect-host">
                      <div className="text-gray-500">redirect_uri host</div>
                      <code className="font-mono text-gray-900 break-all">{xeroDiag.redirect_uri_host || '(empty)'}</code>
                    </div>

                    <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-redirect-full">
                      <div className="text-gray-500">redirect_uri (full)</div>
                      <code className="font-mono text-gray-900 break-all">{xeroDiag.redirect_uri || '(empty)'}</code>
                    </div>

                    <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-redirect-repr">
                      <div className="text-gray-500">redirect_uri repr (whitespace check)</div>
                      <code className="font-mono text-gray-900 break-all">{xeroDiag.redirect_uri_repr || '(empty)'}</code>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]">
                        <div className="text-gray-500">client_id tail</div>
                        <code className="font-mono text-gray-900">{xeroDiag.client_id_tail || '(empty)'}</code>
                      </div>
                      <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]">
                        <div className="text-gray-500">webhook key tail</div>
                        <code className="font-mono text-gray-900">{xeroDiag.webhook_key_tail || '(empty)'}</code>
                      </div>
                    </div>

                    <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-request-host">
                      <div className="text-gray-500">request host (proves which env)</div>
                      <code className="font-mono text-gray-900 break-all">{xeroDiag.request_host || '(empty)'}</code>
                    </div>

                    <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-trailing-slash">
                      <div className="text-gray-500">trailing slash?</div>
                      <code className="font-mono text-gray-900">{String(xeroDiag.redirect_uri_has_trailing_slash)}</code>
                    </div>

                    {xeroAuthUrl && (
                      <div className="rounded px-2 py-1.5 border bg-white border-gray-200 text-[11px]" data-testid="xero-diag-auth-url">
                        <div className="text-gray-500">auth_url being sent to Xero</div>
                        <code className="font-mono text-gray-900 break-all">{xeroAuthUrl}</code>
                      </div>
                    )}
                  </div>

                  <Button
                    size="sm"
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={copyDiagBundle}
                    data-testid="xero-diag-copy-btn"
                  >
                    Copy diag bundle (paste into support)
                  </Button>

                  {/* Fallback textarea for mobile browsers that block
                      navigator.clipboard. The admin can long-press +
                      Copy from this box if the button above fails. */}
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-violet-800 hover:underline">
                      Raw JSON (long-press to copy on mobile)
                    </summary>
                    <textarea
                      id="xero-diag-bundle-textarea"
                      readOnly
                      className="mt-1 w-full h-40 font-mono text-[10px] p-2 bg-white border border-gray-200 rounded"
                      value={JSON.stringify({
                        environment: typeof window !== 'undefined' ? window.location.host : null,
                        fetched_at: new Date().toISOString(),
                        xero_status: xeroStatus,
                        xero_diag: xeroDiag,
                        xero_auth_url: xeroAuthUrl,
                      }, null, 2)}
                      data-testid="xero-diag-raw-textarea"
                    />
                  </details>
                </div>
              )}
            </div>

            {xeroTestResult && (
              <div
                className={`p-3 rounded-md text-xs border ${
                  xeroTestResult.success
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
                data-testid="xero-test-result"
              >
                {xeroTestResult.success ? (
                  <>
                    <div className="font-medium flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Live connection OK</div>
                    <div className="mt-1 text-[11px]">
                      {xeroTestResult.organization} · {xeroTestResult.country} · {xeroTestResult.base_currency}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Connection failed</div>
                    <div className="mt-1 text-[11px]">{xeroTestResult.error}</div>
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <Button
                onClick={handleConnectXero}
                disabled={xeroBusy || !xeroStatus?.configured}
                className="bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="xero-connect-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                {xeroStatus?.connected && !xeroStatus?.needs_reauth ? 'Reconnect to Xero' : 'Connect to Xero'}
              </Button>
              {xeroStatus?.connected && !xeroStatus?.needs_reauth && (
                <>
                  <Button variant="outline" onClick={handleRefreshXero} disabled={xeroBusy} data-testid="xero-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${xeroBusy ? 'animate-spin' : ''}`} /> Refresh Token
                  </Button>
                  <Button variant="outline" onClick={handleTestXero} disabled={xeroBusy} data-testid="xero-test-btn">
                    <Zap className="w-4 h-4 mr-1.5" /> Test Connection
                  </Button>
                  <Button variant="outline" onClick={handleDisconnectXero} disabled={xeroBusy} className="text-red-600 border-red-200" data-testid="xero-disconnect-btn">
                    <LogOut className="w-4 h-4 mr-1.5" /> Disconnect
                  </Button>
                </>
              )}
              {xeroStatus && !xeroStatus.connected && (
                <Button variant="outline" onClick={handleDisconnectXero} disabled={xeroBusy} className="text-red-600 border-red-200" data-testid="xero-disconnect-btn">
                  <LogOut className="w-4 h-4 mr-1.5" /> Clear stored credentials
                </Button>
              )}
            </div>

            <p className="text-[11px] text-gray-400">
              Scopes requested: <span className="font-mono">{xeroStatus?.scopes_requested || '—'}</span>
            </p>
          </CardContent>
        </Card>

        {/* ──────── Yardstik ──────── */}
        <Card data-testid="yardstik-card">
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-base">Yardstik — Background Checks</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Candidate screening for talent onboarding and eligibility checks.
                </CardDescription>
              </div>
            </div>
            <Badge
              className={
                yardstikStatus?.enabled && !yardstikStatus?.mock_mode
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }
              data-testid="yardstik-status-chip"
            >
              {yardstikLoading
                ? 'Loading…'
                : yardstikStatus?.enabled && !yardstikStatus?.mock_mode
                  ? 'Live'
                  : yardstikStatus?.enabled
                    ? 'Enabled'
                    : 'Not configured'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            {yardstikLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Row label="Provider" value={yardstikStatus?.provider || '—'} />
                <Row label="Enabled" value={yardstikStatus?.enabled ? 'Yes' : 'No'} />
                <Row label="Base URL" value={yardstikStatus?.base_url || '—'} mono />
                <Row label="Mock mode" value={yardstikStatus?.mock_mode ? 'Yes' : 'No'} />
                <Row
                  label="Packages"
                  value={(yardstikStatus?.packages_configured || []).join(', ') || '—'}
                />
              </div>
            )}
            <div className="pt-2 border-t border-gray-100 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate('/admin/background-checks')} data-testid="yardstik-open-btn">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Manage Background Checks
              </Button>
              <Button variant="outline" onClick={loadYardstikStatus} disabled={yardstikLoading}>
                <RefreshCw className={`w-4 h-4 mr-1.5 ${yardstikLoading ? 'animate-spin' : ''}`} /> Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ──────── Enterprise API (Phase 2 preview) ──────── */}
        <Card data-testid="enterprise-api-card" className="opacity-80">
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <KeyRound className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">Enterprise API Access</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Issue scoped API keys so enterprise clients can read gigs/bookings and post new gigs.
                </CardDescription>
              </div>
            </div>
            <Badge className="bg-gray-100 text-gray-500">Phase 2 — Coming</Badge>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500">
              Ship plan: hashed-secret issuance, revoke/rotate, usage logs, rate-limit hooks, and read + limited-write endpoints
              (<span className="font-mono">GET /v1/gigs</span>, <span className="font-mono">GET /v1/bookings</span>,
              <span className="font-mono"> POST /v1/gigs</span>) scoped to the enterprise account.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className={`text-gray-800 truncate ${mono ? 'font-mono text-[11px]' : ''}`} title={String(value)}>
        {value}
      </span>
    </div>
  );
}

export default AdminIntegrations;
